import { Injectable } from '@angular/core';
import { StorageService } from '../storage';
import { USER_ID_KEY } from '@tacliatech/types';

export enum UserInstallationState {
  FIRST_TIME_USER = 'first_time_user',
  RETURNING_USER_NO_SESSION = 'returning_user_no_session',
  RETURNING_USER_WITH_SESSION = 'returning_user_with_session',
}

@Injectable({
  providedIn: 'root',
})
export class InstallationStateService {
  private hasEverRegisteredKey = 'hasEverRegistered';
  private hasEverLoggedInKey = 'hasEverLoggedIn';

  /**
   * Determines the user installation state for mobile apps
   */
  async getUserInstallationState(): Promise<UserInstallationState> {
    try {
      const hasActiveSession = !!StorageService.GetItem(USER_ID_KEY);

      if (hasActiveSession) {
        return UserInstallationState.RETURNING_USER_WITH_SESSION;
      }

      const hasEverRegistered = this.getHasEverRegistered();
      const hasEverLoggedIn = this.getHasEverLoggedIn();

      if (hasEverRegistered || hasEverLoggedIn) {
        return UserInstallationState.RETURNING_USER_NO_SESSION;
      }

      return UserInstallationState.FIRST_TIME_USER;
    } catch (error) {
      console.error('Error determining user installation state:', error);
      // Default to first time user if we can't determine state
      return UserInstallationState.FIRST_TIME_USER;
    }
  }

  /**
   * Mark that user has registered at least once
   */
  markUserHasRegistered(): void {
    localStorage.setItem(this.hasEverRegisteredKey, 'true');
  }

  /**
   * Mark that user has logged in at least once
   */
  markUserHasLoggedIn(): void {
    localStorage.setItem(this.hasEverLoggedInKey, 'true');
  }

  /**
   * Clear all installation state (useful for testing or account deletion)
   */
  clearInstallationState(): void {
    localStorage.removeItem(this.hasEverRegisteredKey);
    localStorage.removeItem(this.hasEverLoggedInKey);
  }

  private getHasEverRegistered(): boolean {
    return localStorage.getItem(this.hasEverRegisteredKey) === 'true';
  }

  private getHasEverLoggedIn(): boolean {
    return localStorage.getItem(this.hasEverLoggedInKey) === 'true';
  }
}
